"use client";

import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { formatCompactNumber } from "@/utils/number";

/**
 * 差分表示モードの型定義
 * @description テーブルの差分カラムで使用される表示モード
 */
export type DiffDisplayMode = "absolute" | "percentage";

/**
 * 差分表示の設定に関する定数
 */
const DIFF_DISPLAY_CONSTANTS = {
  /** パーセント表示の小数点以下桁数 */
  PERCENTAGE_DECIMAL_PLACES: 1,
  /** パーセントポイント表記の小数点以下桁数 */
  PERCENT_POINT_DECIMAL_PLACES: 2,
  /** 数値表示の小数点以下桁数（平均ページロケーション等） */
  NUMERIC_DECIMAL_PLACES: 2,
} as const;

type DiffCellProps = {
  invertColor?: boolean;
  /** 増減を良否と解釈しない表示面では中立色を使う。 */
  neutralColor?: boolean;
} & (
  | {
      /** ドメインで算出済みの有限な差。単位変換を含む書式はformatterが所有する。 */
      difference: number;
      formatter: (value: number) => ReactNode;
      base?: never;
      compared?: never;
      displayMode?: never;
      isPercentageMetric?: never;
    }
  | {
      difference?: never;
      base: number | null | undefined;
      compared: number | null | undefined;
      formatter?: (value: number) => string;
      displayMode?: DiffDisplayMode;
      isPercentageMetric?: boolean;
    }
);

/**
 * 数値の差分を表示するセルコンポーネント。
 * 基準値と比較値を比較し、差分を色分けして表示します。
 * ドメインが計算を所有する場合はdifferenceとformatterを渡し、再計算せず表示します。
 *
 * @param {object} props - コンポーネントのプロパティ。
 * @param {number} props.base - 基準値。
 * @param {number} props.compared - 比較値。
 * @param {(value: number) => ReactNode} [props.formatter] - 差分値をフォーマットする関数 (オプション)。
 * @param {boolean} [props.invertColor] - 色の反転フラグ (オプション)。trueの場合、正の差分を赤、負の差分を緑で表示。
 * @param {DiffDisplayMode} [props.displayMode] - 表示モード (オプション)。"absolute"（デフォルト）または"percentage"。
 * @param {boolean} [props.isPercentageMetric] - パーセント指標フラグ (オプション)。trueの場合、差分をパーセントポイント（pp）で表示。
 * @returns {JSX.Element} 差分表示セル。
 */
export function DiffCell({
  difference,
  base,
  compared,
  formatter,
  invertColor,
  neutralColor = false,
  displayMode = "absolute",
  isPercentageMetric = false,
}: DiffCellProps) {
  const { t } = useLingui();
  if (
    difference === undefined &&
    (base === null ||
      base === undefined ||
      compared === null ||
      compared === undefined)
  ) {
    return (
      <div className="text-muted-foreground text-right font-mono tabular-nums">
        N/A
      </div>
    );
  }

  const baseValue = base ?? 0;
  const comparedValue = compared ?? 0;
  const diff = difference ?? baseValue - comparedValue;

  // 表示モードに応じた値と フォーマッターを設定
  let displayValue: number | string = diff;
  let defaultFormatter: (value: number | string) => string;

  if (difference !== undefined) {
    defaultFormatter = String;
  } else if (displayMode === "percentage") {
    if (isPercentageMetric) {
      // パーセント指標の変化率を計算（例：CTR 3% → 5%なら+66.7%）
      if (comparedValue === 0) {
        displayValue = baseValue === 0 ? "0%" : t(msg`N/A`);
        defaultFormatter = (value: number | string) => String(value);
      } else {
        const percentageChange =
          ((baseValue - comparedValue) / comparedValue) * 100;
        displayValue = percentageChange;
        defaultFormatter = (value: number | string) => {
          if (typeof value === "string") return value;
          if (value === 0) return "0%";
          const formatted = Number.isInteger(value)
            ? value.toString()
            : value.toFixed(DIFF_DISPLAY_CONSTANTS.PERCENTAGE_DECIMAL_PLACES);
          if (value > 0) return `+${formatted}%`;
          return `${formatted}%`;
        };
      }
    } else {
      // 通常のメトリクスの場合は変化率を計算
      if (comparedValue === 0) {
        displayValue = baseValue === 0 ? "0%" : t(msg`N/A`);
        defaultFormatter = (value: number | string) => String(value);
      } else {
        const percentageChange =
          ((baseValue - comparedValue) / comparedValue) * 100;
        displayValue = percentageChange;
        defaultFormatter = (value: number | string) => {
          if (typeof value === "string") return value;
          if (value === 0) return "0%";
          // 整数の場合は小数点を表示しない
          const formatted = Number.isInteger(value)
            ? value.toString()
            : value.toFixed(DIFF_DISPLAY_CONSTANTS.PERCENTAGE_DECIMAL_PLACES);
          if (value > 0) return `+${formatted}%`;
          return `${formatted}%`;
        };
      }
    }
  } else {
    // absoluteモード（デフォルト）
    if (isPercentageMetric) {
      // パーセント指標のabsoluteモード
      // GraphQLから来るデータは小数形式（例：0.055 = 5.5%）なので、
      // パーセントポイントの差を計算するには100倍する必要がある
      displayValue = diff * 100;
      defaultFormatter = (value: number | string) => {
        if (typeof value === "string") return value;
        if (value === 0) return "0pp";
        const formatted = Math.abs(value as number).toFixed(
          DIFF_DISPLAY_CONSTANTS.PERCENT_POINT_DECIMAL_PLACES,
        );
        if ((value as number) > 0) return `+${formatted}pp`;
        return `-${formatted}pp`;
      };
    } else {
      defaultFormatter = (value: number | string) => {
        if (typeof value === "string") return value;
        if (value === 0) return "0";

        const numericValue = value as number;
        const magnitude = Math.abs(numericValue);
        // 1,000以上はK/M/Bなどの省略表記で簡潔に表示。それ未満は従来通りローカライズ数値。
        const formattedMagnitude =
          magnitude >= 1000
            ? formatCompactNumber(magnitude)
            : magnitude.toLocaleString();

        const sign = numericValue > 0 ? "+" : "-";
        return `${sign}${formattedMagnitude}`;
      };
    }
  }

  formatter = formatter ?? defaultFormatter;

  const positiveColor = invertColor
    ? "text-red-600 dark:text-red-400"
    : "text-green-700 dark:text-green-400";
  const negativeColor = invertColor
    ? "text-green-700 dark:text-green-400"
    : "text-red-600 dark:text-red-400";

  // 色判定は実際の差分値（diff）で行う
  const isPositive = diff > 0;
  const isZero = diff === 0;
  const isNAValue =
    typeof displayValue === "string" &&
    (displayValue === "N/A" || displayValue.includes("N/A"));

  return (
    <div
      className={cn(
        neutralColor || isNAValue || isZero
          ? "text-muted-foreground text-right"
          : isPositive
            ? `${positiveColor} text-right`
            : `${negativeColor} text-right`,
        "text-right overflow-hidden font-mono tabular-nums",
      )}
    >
      {typeof displayValue === "string"
        ? displayValue
        : formatter(displayValue)}
    </div>
  );
}
